import { Component } from '@theme/component';
import { SlideshowSelectEvent, ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/** Slides after the visible one that are requested ahead of a swipe. */
const WARM_AHEAD = 2;

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Scroll to the selected variant's photo when the page loads with a specific variant
    // pre-selected (e.g. via URL). On carousel mode with admin order, this ensures the
    // correct variant photo is visible without reordering the slides.
    this.#scrollToVariantMedia();

    if (document.readyState === 'complete') {
      this.#scheduleWarmUp();
    } else {
      window.addEventListener('load', this.#scheduleWarmUp, { once: true, signal });
    }
  }

  #controller = new AbortController();

  /**
   * Media after the first one is rendered with `loading="lazy"` so it stays off the LCP's
   * critical path (see snippets/product-media-gallery-content.liquid). Safari, however, does not
   * load a lazy image sitting outside a horizontal scroller until it is swiped into view, which
   * showed an empty slide for a moment. Once the page has loaded and the browser is idle, the
   * next `WARM_AHEAD` slides are requested at low priority, and again after every slide change.
   *
   * Until 2026-10-28 every photo of the gallery was requested here: 0.5–1 MB on a phone that may
   * never swipe, plus the desktop grid, where native lazy loading already fetches each photo
   * before it scrolls into view. The zoom dialog keeps its own lazy images.
   */
  #scheduleWarmUp = () => {
    const start = () => {
      if (this.#controller.signal.aborted) return;

      this.#warmSlides(this.slideshow?.current ?? 0);
      this.addEventListener(
        SlideshowSelectEvent.eventName,
        (event) => {
          // The zoom dialog's slideshow bubbles the same event: only the gallery's own counts.
          if (event.target !== this.slideshow) return;
          this.#warmSlides(/** @type {SlideshowSelectEvent} */ (event).detail.index);
        },
        { signal: this.#controller.signal }
      );
    };

    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(start, { timeout: 3000 });
    } else {
      setTimeout(start, 1200);
    }
  };

  /**
   * Requests the slide at `index` and the `WARM_AHEAD` after it.
   * @param {number} index
   */
  #warmSlides(index) {
    const slides = this.slideshow?.querySelectorAll('slideshow-slide');
    if (!slides) return;

    const last = Math.min(slides.length - 1, index + WARM_AHEAD);
    for (let i = Math.max(0, index); i <= last; i++) {
      for (const image of slides[i].querySelectorAll('img[loading="lazy"]')) {
        image.setAttribute('fetchpriority', 'low');
        image.setAttribute('loading', 'eager');
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = /** @type {HTMLElement | null} */ (source.querySelector('media-gallery'));

    if (!newMediaGallery) return;

    if (this.#showVariantSlide(newMediaGallery)) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * ro.ro: with the block setting `variant_media_last` the gallery holds every photo of the
   * product whatever the variant, and only the one it opens with changes (the selected
   * variant's photo). If the incoming gallery has exactly the same photos, it is not swapped in:
   * the slideshow jumps to the photo the new one opens with, so the sequence never reshuffles.
   *
   * The jump is instant on purpose. Horizon's slideshow ignores any `select()` while an animated
   * jump of more than one slide is running, so two quick taps on the variant circles would leave
   * the photo of the first one on screen.
   *
   * The desktop grid shows every photo at once, so there the gallery is still swapped in and the
   * selected variant's photo moves to the top; the rest of the sequence keeps its order.
   *
   * @param {HTMLElement} newMediaGallery - The gallery rendered by the server for the new variant.
   * @returns {boolean} Whether the update was handled without replacing the gallery.
   */
  #showVariantSlide(newMediaGallery) {
    const { slideshow } = this;
    if (!slideshow) return false;
    if (this.presentation === 'grid' && window.matchMedia('(min-width: 750px)').matches) return false;

    /** @param {Element} root */
    const slideIds = (root) =>
      Array.from(root.querySelectorAll('slideshow-slide[slide-id]'), (slide) => slide.getAttribute('slide-id'));

    const currentIds = slideIds(slideshow);
    const nextIds = slideIds(newMediaGallery);
    const targetId = newMediaGallery.dataset.variantMediaId || nextIds[0];

    if (!targetId || currentIds.length < 2 || currentIds.length !== nextIds.length) return false;

    const current = new Set(currentIds);
    if (!nextIds.every((id) => current.has(id))) return false;

    slideshow.select({ id: targetId }, undefined, { animate: false });

    // The badge on the photo (snippets/roro-product-badge.liquid) can depend on the variant
    // (sale price), and it is the only part of the gallery that does.
    const badge = this.querySelector('.pdp-media__badge');
    const nextBadge = newMediaGallery.querySelector('.pdp-media__badge');
    if (badge && nextBadge) badge.replaceWith(nextBadge);
    else if (badge) badge.remove();
    else if (nextBadge) slideshow.before(nextBadge);

    return true;
  }

  /**
   * Scrolls the carousel to the selected variant's photo on initial load.
   * Only needed when the gallery keeps admin order (variant_media_last) and
   * the page is opened with a non-default variant pre-selected via URL.
   */
  #scrollToVariantMedia() {
    const variantMediaId = this.dataset.variantMediaId;
    if (!variantMediaId) return;
    this.refs.slideshow?.select({ id: variantMediaId }, undefined, { animate: false });
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
